// First-party autocapture tracking (client-side).
//
// One module installs global listeners and captures page views, every click,
// scroll depth, and half-filled forms — with zero per-button / per-form code
// elsewhere. Events are batched and flushed to /api/track (which stamps user_id
// + ip server-side) and mirrored to GA4. All best-effort: a failure here must
// never break a page or a flow.
//
// Public API: initTracking() (idempotent, called once from <Tracker/>) and
// page() (called on route change). Everything else is automatic.

type Json = Record<string, unknown>;

interface QueuedEvent {
  anon_id: string;
  session_id: string;
  event: string;
  props: Json;
  path: string;
  referrer: string;
  utm: Json;
  ts: number;
}

const ANON_COOKIE = 'gtrk_anon';
const FT_COOKIE = 'gtrk_ft'; // first-touch attribution
const SESSION_KEY = 'gtrk_sess';
const ANON_LS = 'gtrk_anon';

const FLUSH_AT = 10; // queue length that triggers a flush
const FLUSH_EVERY_MS = 5000;
// Presence heartbeat while the tab is visible — powers the admin "live now"
// indicator (bumps a short-TTL Redis key server-side; not stored in Postgres).
const HEARTBEAT_EVERY_MS = 30000;
const VALUE_MAX = 200; // max chars stored for a form field value
const TEXT_MAX = 80; // max chars stored for click text

// Fields whose values must NEVER be captured (secrets / OTP / payment).
const SENSITIVE_RE = /otp|code|card|cvv|cvc|pin|password|passwd|secret|token/i;
const SENSITIVE_TYPES = new Set(['password', 'hidden', 'file']);

// ---------------------------------------------------------------------------
// small cookie / storage helpers (all guarded, best-effort)
// ---------------------------------------------------------------------------
function getCookie(name: string): string {
  try {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  } catch {
    return '';
  }
}

function setCookie(name: string, value: string, maxAgeSec: number): void {
  try {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSec}; SameSite=Lax`;
  } catch {
    /* ignore */
  }
}

function uuid(): string {
  try {
    if (crypto?.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  // RFC4122-ish fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// ---------------------------------------------------------------------------
// identity + attribution
// ---------------------------------------------------------------------------
function getAnonId(): string {
  let id = getCookie(ANON_COOKIE);
  if (!id) {
    try {
      id = localStorage.getItem(ANON_LS) || '';
    } catch {
      /* ignore */
    }
  }
  if (!id) id = uuid();
  setCookie(ANON_COOKIE, id, 365 * 24 * 60 * 60); // 1 year
  try {
    localStorage.setItem(ANON_LS, id);
  } catch {
    /* ignore */
  }
  return id;
}

function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY) || '';
    if (!id) {
      id = uuid();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return uuid();
  }
}

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
];

// First-touch attribution: captured once, then persisted and never overwritten.
function getFirstTouch(): Json {
  const stored = getCookie(FT_COOKIE);
  if (stored) {
    try {
      return JSON.parse(stored) as Json;
    } catch {
      /* re-capture below */
    }
  }
  const params = new URLSearchParams(window.location.search);
  const ft: Json = { referrer: document.referrer || '', landing: window.location.pathname };
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v) ft[k] = v.slice(0, 120);
  }
  setCookie(FT_COOKIE, JSON.stringify(ft), 365 * 24 * 60 * 60);
  return ft;
}

// ---------------------------------------------------------------------------
// module state
// ---------------------------------------------------------------------------
let inited = false;
let anonId = '';
let sessionId = '';
let firstTouch: Json = {};
let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

// per-page scroll state
let maxDepth = 0;
let pageEnteredAt = 0;
const scrollMilestonesSent = new Set<number>();

// debounce timers per form field (keyed by form|field)
const formTimers = new Map<string, ReturnType<typeof setTimeout>>();

function dntEnabled(): boolean {
  try {
    const v =
      navigator.doNotTrack ||
      (window as unknown as { doNotTrack?: string }).doNotTrack ||
      (navigator as unknown as { msDoNotTrack?: string }).msDoNotTrack;
    return v === '1' || v === 'yes';
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// enqueue + flush
// ---------------------------------------------------------------------------
function enqueue(event: string, props: Json): void {
  try {
    const ft = firstTouch as { referrer?: string };
    queue.push({
      anon_id: anonId,
      session_id: sessionId,
      event,
      props,
      path: window.location.pathname,
      referrer: ft.referrer || '',
      utm: firstTouch,
      ts: Date.now(),
    });
    // Mirror to GA4 (existing gtag from Analytics.tsx), best-effort.
    const g = (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag;
    if (g) g('event', event, props);
    if (queue.length >= FLUSH_AT) flush();
  } catch {
    /* ignore */
  }
}

function flush(): void {
  if (!queue.length) return;
  const batch = queue;
  queue = [];
  try {
    const body = JSON.stringify({ events: batch });
    if (navigator.sendBeacon) {
      const ok = navigator.sendBeacon('/api/track', new Blob([body], { type: 'application/json' }));
      if (ok) return;
    }
    // fallback keeps the request alive past unload
    fetch('/api/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* drop the batch rather than throw */
  }
}

// ---------------------------------------------------------------------------
// autocapture: click
// ---------------------------------------------------------------------------
function onClick(e: MouseEvent): void {
  try {
    const start = e.target as Element | null;
    if (!start) return;
    const el =
      (start.closest('a,button,[role="button"],[data-track]') as HTMLElement | null) ||
      (start as HTMLElement);
    const tag = el.tagName ? el.tagName.toLowerCase() : '';
    const text = (el.innerText || el.textContent || '').trim().slice(0, TEXT_MAX);
    const props: Json = { tag };
    if (text) props.text = text;
    if (el.id) props.id = el.id;
    const href = (el as HTMLAnchorElement).href;
    if (href) props.href = href.slice(0, 300);
    const dt = el.getAttribute?.('data-track');
    if (dt) props.name = dt;
    enqueue('click', props);
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// autocapture: scroll depth + time on page
// ---------------------------------------------------------------------------
let scrollScheduled = false;
function onScroll(): void {
  if (scrollScheduled) return;
  scrollScheduled = true;
  requestAnimationFrame(() => {
    scrollScheduled = false;
    try {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      const pct = scrollable > 0 ? Math.round((window.scrollY / scrollable) * 100) : 100;
      if (pct > maxDepth) maxDepth = Math.min(pct, 100);
      for (const m of [25, 50, 75, 100]) {
        if (maxDepth >= m && !scrollMilestonesSent.has(m)) {
          scrollMilestonesSent.add(m);
          enqueue('scroll', { depth: m });
        }
      }
    } catch {
      /* ignore */
    }
  });
}

function sendScrollSummary(): void {
  try {
    if (maxDepth > 0 || pageEnteredAt) {
      enqueue('scroll', {
        summary: true,
        maxDepth,
        timeOnPageMs: pageEnteredAt ? Date.now() - pageEnteredAt : 0,
      });
    }
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// autocapture: form fields (half-filled recovery), sensitive fields excluded
// ---------------------------------------------------------------------------
function isSensitive(el: HTMLInputElement): boolean {
  if (SENSITIVE_TYPES.has(el.type)) return true;
  const hay = `${el.name || ''} ${el.id || ''} ${el.getAttribute('autocomplete') || ''}`;
  return SENSITIVE_RE.test(hay);
}

function formIdOf(el: HTMLElement): string {
  const form = (el as HTMLInputElement).form;
  return (
    form?.getAttribute('id') ||
    form?.getAttribute('name') ||
    form?.getAttribute('data-form') ||
    window.location.pathname
  );
}

function onFormInput(e: Event): void {
  try {
    const el = e.target as HTMLInputElement | null;
    if (!el || !('value' in el)) return;
    const tag = el.tagName?.toLowerCase();
    if (tag !== 'input' && tag !== 'textarea' && tag !== 'select') return;
    if (isSensitive(el)) return;

    const field = el.name || el.id || el.getAttribute('placeholder') || 'field';
    const form = formIdOf(el);
    const key = `${form}|${field}`;
    const prev = formTimers.get(key);
    if (prev) clearTimeout(prev);
    formTimers.set(
      key,
      setTimeout(() => {
        formTimers.delete(key);
        const raw = (el.value ?? '').toString();
        enqueue('form_change', {
          form,
          field,
          type: el.type || tag,
          value: raw.slice(0, VALUE_MAX),
        });
      }, 700),
    );
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// page view (called on init + every route change)
// ---------------------------------------------------------------------------
export function page(): void {
  if (!inited || dntEnabled()) return;
  // finalise the previous page's scroll summary before starting a new one
  sendScrollSummary();
  maxDepth = 0;
  pageEnteredAt = Date.now();
  scrollMilestonesSent.clear();
  const params = new URLSearchParams(window.location.search);
  const props: Json = { title: document.title };
  const q: Json = {};
  params.forEach((v, k) => {
    q[k] = v.slice(0, 120);
  });
  if (Object.keys(q).length) props.query = q;
  enqueue('page_view', props);
}

// ---------------------------------------------------------------------------
// init (idempotent)
// ---------------------------------------------------------------------------
export function initTracking(): void {
  if (inited || typeof window === 'undefined') return;
  if (dntEnabled()) {
    inited = true; // mark done so page() no-ops; respect DNT — no first-party POST
    return;
  }
  inited = true;
  try {
    anonId = getAnonId();
    sessionId = getSessionId();
    firstTouch = getFirstTouch();

    document.addEventListener('click', onClick, { capture: true, passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('input', onFormInput, { capture: true, passive: true });
    document.addEventListener('change', onFormInput, { capture: true, passive: true });

    const flushOnHide = () => {
      if (document.visibilityState === 'hidden') {
        sendScrollSummary();
        flush();
      }
    };
    document.addEventListener('visibilitychange', flushOnHide);
    window.addEventListener('pagehide', () => {
      sendScrollSummary();
      flush();
    });

    flushTimer = setInterval(flush, FLUSH_EVERY_MS);
    heartbeatTimer = setInterval(() => {
      if (document.visibilityState === 'visible') enqueue('heartbeat', {});
    }, HEARTBEAT_EVERY_MS);

    // first page view for the entry page
    page();
  } catch {
    /* ignore — never break the app for analytics */
  }
}

// Exposed for completeness / tests; not required by callers.
export function _stopTracking(): void {
  if (flushTimer) clearInterval(flushTimer);
  flushTimer = null;
  if (heartbeatTimer) clearInterval(heartbeatTimer);
  heartbeatTimer = null;
}
